import { useCallback, useEffect, useMemo, useState } from 'react';
import useBusinessInformationForm from './useBusinessInformationForm';
import { useGetBusinessInformationDetails } from '@src/hooks/queries/onboarding';
import useBusinessDocumentationForm from './useBusinessDocumentationForm';
import { getBeneficialOwnerInitialValues } from '../utils/getBeneficialOwnerInitialValues';
import { handleValidateBeneficialOwnerData } from '../utils/handleBeneficialOwnerData';
import useDebounce from '@src/hooks/utils/useDebounce';
import { useAccount } from '@src/contexts';
import Api from '@sendsprint/api-types';

interface IUseOnboardingVerificationCheckOptions {
  isBusinessComplete: boolean | undefined;
}

const useOnboardingVerificationCheck = ({
  isBusinessComplete
}: IUseOnboardingVerificationCheckOptions) => {
  const [isBeneficialOwnerSet, setIsBeneficialOwnerSet] = useState(false);
  const [isBeneficialOwnersVerified, setIsBeneficialOwnersVerified] = useState(false);

  const { userAddress } = useAccount();

  const { data: businessInformationDetails, isLoading: businessInformationDetailsLoading } =
    useGetBusinessInformationDetails({ enabled: !!isBusinessComplete });

  const {
    initialValues: businessInformationInitialValues,
    validationSchema: businessInformationValidationSchema,
    businessIndustriesLoading,
    businessTypesLoading
  } = useBusinessInformationForm({
    businessInformationDetails
  });

  const {
    initialValues: businessDocumentationInitialValues,
    validationSchema: businessDocumentationValidationSchema
  } = useBusinessDocumentationForm({
    businessInformationDetails,
    agreementValue: true
  });

  const beneficialOwnersInitialValues = useMemo(
    () => getBeneficialOwnerInitialValues(businessInformationDetails || null),
    [businessInformationDetails]
  );

  const debouncedBusinessInformationDetailsLoading = useDebounce(businessInformationDetailsLoading);

  const isBusinessInformationVerified = useMemo(() => {
    try {
      if (
        businessIndustriesLoading ||
        businessTypesLoading ||
        businessInformationDetailsLoading ||
        !businessInformationDetails
      )
        return false;

      return businessInformationValidationSchema.isValidSync(businessInformationInitialValues);
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }, [
    businessInformationInitialValues,
    businessInformationValidationSchema,
    businessIndustriesLoading,
    businessTypesLoading,
    businessInformationDetailsLoading,
    businessInformationDetails
  ]);

  const isBusinessDocumentationVerified = useMemo(() => {
    try {
      if (businessInformationDetailsLoading || !businessInformationDetails) return false;

      return businessDocumentationValidationSchema.isValidSync(businessDocumentationInitialValues);
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }, [
    businessDocumentationInitialValues,
    businessDocumentationValidationSchema,
    businessInformationDetailsLoading,
    businessInformationDetails
  ]);

  const handleValidateBusinessOwner = useCallback(async () => {
    try {
      if (businessInformationDetailsLoading || !businessInformationDetails)
        // if (businessInformationDetailsLoading || !businessInformationDetails || isBeneficialOwnerSet)
        return;

      const { fullErrorObj } = await handleValidateBeneficialOwnerData(
        beneficialOwnersInitialValues,
        userAddress?.country as Api.Model.CountryInitials
      );

      setIsBeneficialOwnersVerified(!fullErrorObj);
      setIsBeneficialOwnerSet(true);
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }, [
    beneficialOwnersInitialValues,
    businessInformationDetails,
    isBeneficialOwnerSet,
    businessInformationDetailsLoading
  ]);

  useEffect(() => {
    handleValidateBusinessOwner();
  }, [handleValidateBusinessOwner]);

  const isOnboardingValidated =
    isBeneficialOwnersVerified && isBusinessDocumentationVerified && isBusinessInformationVerified;

  const debouncedIsOnboardingValidated = useDebounce(isOnboardingValidated);

  return {
    isOnboardingValidated: debouncedIsOnboardingValidated,
    businessInformationDetailsLoading:
      debouncedBusinessInformationDetailsLoading ||
      businessInformationDetailsLoading ||
      businessIndustriesLoading ||
      businessTypesLoading
  };
};

export default useOnboardingVerificationCheck;
